/**
 * ExternalLoadHistograms – histogram view for the External Loads tab.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next'
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Loader2,
  Gauge, Activity, Flame, AlertTriangle, BarChart3, Zap, ArrowDownRight, ArrowUpRight, Route,
  Globe2, Dumbbell, Sun, Trophy, Timer, CircleDot, CalendarDays,
  ArrowUpAZ, ArrowUp01, Sunrise, Moon, Check,
} from "lucide-react";
import HistogramChart from "./HistogramChart.jsx";
import MetricVisibilitySidebar from "./MetricVisibilitySidebar";
import {
  useExternalLoadDrills,
  GLOBAL_DRILL,
  ALL_PERIODS,
} from "./useExternalLoadDrills.js";
import { normalizePlayerPosition } from "../common/PlayerPositions.jsx";


const POSITIONS = [
  { value: "All",      labelKey: "positions.all"      },
  { value: "Defense",  labelKey: "positions.defense"  },
  { value: "Midfield", labelKey: "positions.midfield" },
  { value: "Forward",  labelKey: "positions.forward"  },
  { value: "Lateral",  labelKey: "positions.lateral"  },
];

const sortAlpha = (a, b) => {
  const cmp = (a.last_name ?? "").localeCompare(b.last_name ?? "");
  return cmp !== 0 ? cmp : (a.first_name ?? "").localeCompare(b.first_name ?? "");
};





const toNumberOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isPositiveNumber = (v) => {
  const n = toNumberOrNull(v);
  return n !== null && n > 0;
};

const normalizeMetricValue = (metric, rawValue, isLatestMode) => {
  const n = toNumberOrNull(rawValue);
  if (n === null) return null;
  if (metric.key === "global_distance") {
    return isLatestMode ? n : n / 1000;
  }
  return n;
};

const DEFAULT_ORDER = [
  "global_distance", "hsr_distance_m", "spr_distance_m", "sprint_max", "sprint_count",
  "COD_number", "acceleration_max", "deceleration_max", "accel_count", "decel_count",
];

export default function ExternalLoadHistograms({
  
  players = [],
  isDarkMode = false,
  selectedPosition = "All",
  onPositionChange,
  onPlayerClick,
  season,
  visibleMetrics: visibleMetricsProp,
  onVisibleMetricsChange,
}) {
  const { t } = useTranslation()
  const [highlightId, setHighlightId] = useState(null);
  const [sortMode, setSortMode] = useState("alpha");
  const [toolbarCollapsed, setToolbarCollapsed] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const allowedPlayerIds = useMemo(
    () => new Set(players.map((player) => String(player.id))),
    [players]
  );
  const matchesSelectedPosition = useCallback(
    (position) =>
      selectedPosition === "All" ||
      normalizePlayerPosition(position) === selectedPosition,
    [selectedPosition]
  );

  
function classifyDrill(name) {
  if (!name) return "other";
  for (const cat of DRILL_CATEGORIES) {
    if (cat.regex && cat.regex.test(name)) return cat.key;
  }
  return "other";
}
const METRICS = [
  {
    key: "global_distance",
    drillKey: "distance",
    label: t('metrics.total_distance'),
    unit: "km",
    color: "#64748b",
    icon: Route,
    format: (v) => (v != null ? Number(v).toFixed(2) : "–"),
    fromPlayer: (p) => p.global_distance,
  },
  {
    key: "hsr_distance_m",
    drillKey: "hsr",
     label: t('metrics.hsr'), 
    unit: "m",
    color: "#10b981",
    icon: Activity,
    format: (v) => (v != null ? Math.round(v).toString() : "–"),
    fromPlayer: (p) => p.hsr_distance_m,
  },
  {
    key: "spr_distance_m",
    drillKey: "spr",
    label: "SPR",
    unit: "m",
    color: "#f43f5e",
    icon: Zap,
    format: (v) => (v != null ? Math.round(v).toString() : "–"),
    fromPlayer: (p) => p.spr_distance_m ?? p.SPR ?? null,
  },
  {
    key: "sprint_max",
    drillKey: "sprint_max",
    label: t('metrics.max_sprint_speed'),
    unit: "km/h",
    color: "#059669",
    icon: Zap,
    format: (v) => (v != null ? Number(v).toFixed(2) : "–"),
    fromPlayer: (p) => p.sprint_max,
  },
  {
    key: "sprint_count",
    drillKey: "sprint80_number",
     label: t('metrics.sprint_count'),
    unit: "",
    color: "#8b5cf6",
    icon: Flame,
    format: (v) => (v != null ? Math.round(v).toString() : "–"),
    fromPlayer: (p) => p.sprint_count,
  },
  {
    key: "acceleration_max",
    drillKey: "acc_max",
    label: t('metrics.max_acceleration'),
    unit: "m/s²",
    color: "#6366f1",
    icon: ArrowUpRight,
    format: (v) => (v != null ? Number(v).toFixed(2) : "–"),
    fromPlayer: (p) => p.acceleration_max,
  },
  {
    key: "deceleration_max",
    drillKey: "dec_max",
    label: t('metrics.max_deceleration'),
    unit: "m/s²",
    color: "#f59e0b",
    icon: ArrowDownRight,
    format: (v) => (v != null ? Number(v).toFixed(2) : "–"),
    fromPlayer: (p) => p.deceleration_max,
  },
  {
    key: "accel_count",
    drillKey: "acc_number",
    label: t('metrics.accel_count'), 
    unit: "",
    color: "#818cf8",
    icon: Gauge,
    format: (v) => (v != null ? Math.round(v).toString() : "–"),
    fromPlayer: (p) => p.accel_count,
  },
  {
    key: "decel_count",
    drillKey: "dec_number",
     label: t('metrics.decel_count'),
    unit: "",
    color: "#fbbf24",
    icon: AlertTriangle,
    format: (v) => (v != null ? Math.round(v).toString() : "–"),
    fromPlayer: (p) => p.decel_count,
  },
  {
    key: "COD_number",
    drillKey: "cod_number",
    label: t('metrics.cod'), 
    unit: "",
    color: "#a78bfa",
    icon: BarChart3,
    format: (v) => (v != null ? Math.round(v).toString() : "–"),
    fromPlayer: (p) => p.COD_number,
  },
];

const METRICS_BY_KEY = Object.fromEntries(METRICS.map((m) => [m.key, m]));

const DRILL_CATEGORIES = [
  { key: "__global__", label: t('charts.scope_global'),          icon: Globe2,       regex: null,                               color: "#6366f1" },
  { key: "training",   label: t('external_load.drill_training'), icon: Dumbbell,     regex: /entra[iî]n|training/i,             color: "#3b82f6" },
  { key: "warmup",     label: t('external_load.drill_warmup'),   icon: Sun,          regex: /[eé]chauff|warm/i,                 color: "#f59e0b" },
  { key: "match",      label: t('external_load.drill_match'),    icon: Trophy,       regex: /\bmatch\b|\bjour\s*de\s*match/i,   color: "#ef4444" },
  { key: "mt_md",      label: "MT / MD",                         icon: CalendarDays, regex: /\b(mt|md)[\s\-]?\d/i,              color: "#8b5cf6" },
  { key: "halftime",   label: t('external_load.drill_halftime'), icon: Timer,        regex: /mi[\s\-]?temps|half[\s\-]?time/i,  color: "#10b981" },
  { key: "session",    label: t('external_load.drill_session'),  icon: CircleDot,    regex: /session/i,                         color: "#64748b" },
]
  const [metricOrder, setMetricOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("externalLoad_metricOrder");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (
          Array.isArray(parsed) &&
          parsed.length === DEFAULT_ORDER.length &&
          DEFAULT_ORDER.every((k) => parsed.includes(k)) &&
          parsed.every((k) => DEFAULT_ORDER.includes(k))
        ) {
          return parsed;
        }
      }
    } catch {}
    localStorage.removeItem("externalLoad_metricOrder");
    return DEFAULT_ORDER;
  });

  const [visibleMetricsLocal, setVisibleMetricsLocal] = useState(() => {
    try {
      const saved = sessionStorage.getItem("externalLoad_visibleMetrics");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return new Set(parsed);
      }
    } catch {}
    return new Set(DEFAULT_ORDER);
  });

  const visibleMetrics = visibleMetricsProp ?? visibleMetricsLocal;
  const setVisibleMetrics = (valOrFn) => {
    const next = typeof valOrFn === "function" ? valOrFn(visibleMetrics) : valOrFn;
    if (onVisibleMetricsChange) onVisibleMetricsChange(next);
    else setVisibleMetricsLocal(next);
    sessionStorage.setItem("externalLoad_visibleMetrics", JSON.stringify([...next]));
  };

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return sessionStorage.getItem("externalLoad_sidebarOpen") === "true"; } catch {} return false;
  });

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const containerRef = useRef(null);
  const sentinelRef = useRef(null);

  const handleDragStart = (index) => { dragItem.current = index; };
  const handleDragEnter = (index) => { dragOverItem.current = index; };
  const handleDragEnd = () => {
    const newOrder = [...metricOrder];
    const dragged = newOrder.splice(dragItem.current, 1)[0];
    newOrder.splice(dragOverItem.current, 0, dragged);
    setMetricOrder(newOrder);
    localStorage.setItem("externalLoad_metricOrder", JSON.stringify(newOrder));
    dragItem.current = null;
    dragOverItem.current = null;
  };

  // Detect when toolbar is stuck
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-61px 0px 0px 0px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Auto-expand when not stuck
  useEffect(() => {
    if (!isStuck) setToolbarCollapsed(false);
  }, [isStuck]);

  // Close highlight on outside click
  useEffect(() => {
    if (highlightId == null) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setHighlightId(null);
      }
    };
    document.addEventListener("pointerdown", handleClick);
    return () => document.removeEventListener("pointerdown", handleClick);
  }, [highlightId]);

  const {
    dates,
    loadingDates,
    selectedDate,
    setSelectedDate,
    loadingDrills,
    drillData,
    drillNames,
    selectedDrill,
    setSelectedDrill,
    playerMetrics,
    dataSource,
    availablePeriods,
    selectedPeriod,
    setSelectedPeriod,
  } = useExternalLoadDrills(season);

  const isLatestMode = selectedDate == null;
  const hasDrillData = !isLatestMode && playerMetrics && Object.keys(playerMetrics).length > 0;

  const handleDateChange = useCallback((val) => {
    setSelectedDate(val);
  }, [setSelectedDate]);

  const handleBarClick = useCallback(
    (id) => setHighlightId((prev) => (prev === id ? null : id)),
    []
  );

  const drillIconMap = useMemo(() => {
    const map = {};
    for (const name of drillNames) {
      const catKey = classifyDrill(name);
      const cat = DRILL_CATEGORIES.find((c) => c.key === catKey);
      map[name] = cat?.icon ?? CircleDot;
    }
    return map;
  }, [drillNames]);

  const chartData = useMemo(() => {
    if (isLatestMode || !hasDrillData) {
      return METRICS.map((m) => {
        const arr = players
          .filter((p) => matchesSelectedPosition(p.position))
          .map((p) => {
            const normalizedValue = normalizeMetricValue(m, m.fromPlayer(p), true);
            return {
              id: p.id,
              first_name: p.first_name,
              last_name: p.last_name,
              profile_picture: p.profile_picture,
              position: p.position,
              value: normalizedValue,
              trainability_index: p.trainability_index,
              trainability_level: p.trainability_level,
            };
          })
          .filter((p) => isPositiveNumber(p.value))
          .sort(
            sortMode === "asc"
              ? (a, b) => (a.value ?? -Infinity) - (b.value ?? -Infinity)
              : sortAlpha
          );
        return { metric: m, players: arr };
      });
    }

    const metrics = playerMetrics || {};
    return METRICS.map((m) => {
      const arr = Object.values(metrics)
        .filter(
          (p) =>
            allowedPlayerIds.has(String(p.player_id)) &&
            matchesSelectedPosition(p.position)
        )
        .map((p) => {
          const raw = p.metrics?.[m.drillKey] ?? null;
          const normalizedValue = normalizeMetricValue(m, raw, false);
          return {
            id: p.player_id,
            first_name: p.first_name,
            last_name: p.last_name,
            profile_picture: p.profile_picture || null,
            position: p.position,
            value: normalizedValue,
            trainability_index: p.trainability_index ?? null,
            trainability_level: p.trainability_level ?? null,
          };
        })
        .filter((p) => isPositiveNumber(p.value))
        .sort(
          sortMode === "asc"
            ? (a, b) => (a.value ?? -Infinity) - (b.value ?? -Infinity)
            : sortAlpha
        );
      return { metric: m, players: arr };
    });
  }, [isLatestMode, hasDrillData, players, playerMetrics, sortMode, allowedPlayerIds, matchesSelectedPosition]);

  const enrichedChartData = useMemo(() => {
    if (isLatestMode || !hasDrillData) return chartData;
    const picMap = Object.fromEntries(players.map((p) => [String(p.id), p.profile_picture]));
    const trainMap = Object.fromEntries(
      players.map((p) => [String(p.id), { trainability_index: p.trainability_index, trainability_level: p.trainability_level }])
    );
    return chartData.map(({ metric, players: arr }) => ({
      metric,
      players: arr.map((p) => ({
        ...p,
        profile_picture: p.profile_picture || picMap[String(p.id)] || null,
        trainability_index: p.trainability_index ?? trainMap[String(p.id)]?.trainability_index ?? null,
        trainability_level: p.trainability_level ?? trainMap[String(p.id)]?.trainability_level ?? null,
      })),
    }));
  }, [chartData, isLatestMode, hasDrillData, players]);

  const dataByKey = useMemo(() => {
    const map = {};
    enrichedChartData.forEach(({ metric, players: barPlayers }) => {
      map[metric.key] = barPlayers;
    });
    return map;
  }, [enrichedChartData]);

  const allDates = [...dates];
  const dateIdx = allDates.indexOf(selectedDate);
  const canPrev = dateIdx < allDates.length - 1;
  const canNext = dateIdx > 0;

  const formatNavDate = (iso) => {
    if (!iso) return "–";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
    });
  };

  const isTransitioning = loadingDrills;
  const hasNoData = !loadingDates && !loadingDrills && dates.length === 0 && players.length === 0;

  return (
    <div className="space-y-5">
      {/* Sentinel */}
      <div ref={sentinelRef} className="h-0" />

      {/* ─── Sticky Toolbar ─── */}
      <div
        className={`sticky top-[60px] z-[15] -mx-6 px-6 transition-all duration-300 ${
          toolbarCollapsed ? "pt-1 pb-0" : "pt-3 pb-1"
        } ${isDarkMode ? "bg-gray-900/90 backdrop-blur-md" : "bg-gray-100/90 backdrop-blur-md"}`}
        style={{ position: "sticky" }}
      >
        {isStuck && (
          <div className={`absolute bottom-0 left-4 right-4 h-px ${
            isDarkMode
              ? "bg-gradient-to-r from-transparent via-gray-500/40 to-transparent"
              : "bg-gradient-to-r from-transparent via-gray-400/30 to-transparent"
          }`} />
        )}

        {/* Collapsible content */}
        <div
          className={`overflow-visible transition-all duration-300 ease-in-out ${
            toolbarCollapsed ? "max-h-0 opacity-0 overflow-hidden" : "opacity-100"
          }`}
        >
          {/* ── Row 1 : filters ── */}
          <div className="flex flex-wrap items-center gap-3">

            {/* Position filter */}
            <select
              className={`p-2.5 border rounded-lg text-sm shadow-sm focus:ring focus:ring-blue-300 ${
                isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-800"
              }`}
              value={selectedPosition}
              onChange={(e) => onPositionChange?.(e.target.value)}
            >
     {POSITIONS.map((pos) => (
  <option key={pos.value} value={pos.value}>
    {t(pos.labelKey)}
  </option>
))}
            </select>

            {/* Sort toggle */}
            <div className={`inline-flex items-center rounded-lg border p-0.5 ${
              isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-gray-100"
            }`}>
              <button
                onClick={() => setSortMode("alpha")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  sortMode === "alpha"
                    ? isDarkMode ? "bg-gray-700 text-white shadow-sm" : "bg-white text-gray-900 shadow-sm"
                    : isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
                }`}
                title={t('external_load.sort_alpha')}
              >
                <ArrowUpAZ className="h-3.5 w-3.5" />
                A-Z
              </button>
              <button
                onClick={() => setSortMode("asc")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  sortMode === "asc"
                    ? isDarkMode ? "bg-gray-700 text-white shadow-sm" : "bg-white text-gray-900 shadow-sm"
                    : isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
                }`}
                title={t('external_load.sort_asc')}
              >
                <ArrowUp01 className="h-3.5 w-3.5" />
                0-9
              </button>
            </div>

            {/* Separator */}
            <div className={`h-6 w-px ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`} />

            {/* Date navigator */}
            <div className="flex items-center gap-1.5">
              <button
                disabled={!canPrev || loadingDates}
                onClick={() => canPrev && handleDateChange(allDates[dateIdx + 1])}
                className={`p-1.5 rounded-md transition ${
                  isDarkMode ? "hover:bg-gray-700 disabled:opacity-30 text-gray-300" : "hover:bg-gray-100 disabled:opacity-30 text-gray-600"
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <select
                className={`px-2 py-2.5 rounded-lg border text-sm ${
                  isDarkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-800"
                }`}
                value={selectedDate ?? ""}
                onChange={(e) => handleDateChange(e.target.value)}
              >
                {dates.map((d) => (
                  <option key={d} value={d}>{formatNavDate(d)}</option>
                ))}
              </select>
              <button
                disabled={!canNext || loadingDates}
                onClick={() => canNext && handleDateChange(allDates[dateIdx - 1])}
                className={`p-1.5 rounded-md transition ${
                  isDarkMode ? "hover:bg-gray-700 disabled:opacity-30 text-gray-300" : "hover:bg-gray-100 disabled:opacity-30 text-gray-600"
                }`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>


            {loadingDates && (
              <div className="flex items-center gap-1.5">
                <Loader2 className={`h-4 w-4 animate-spin ${isDarkMode ? "text-gray-400" : "text-gray-500"}`} />
                <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{t('common.loading')}</span>
              </div>
            )}

          </div>{/* end flex row */}

          {/* ── Period filter (AM / PM) ── */}
          {!isLatestMode && availablePeriods.length > 0 && (
            <div className="flex items-center justify-center gap-2 mt-2.5">
              {availablePeriods.map((period) => {
                const isActive = selectedPeriod === period;
                const PeriodIcon = period === "AM" ? Sunrise : Moon;
                const periodLabel = period === "AM" ? t('external_load.morning') : t('external_load.afternoon');
                const periodColor = period === "AM" ? "#f59e0b" : "#6366f1";
                return (
                  <button
                    key={period}
                    onClick={() => setSelectedPeriod(period)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                      isActive
                        ? "text-white shadow-md scale-[1.02]"
                        : isDarkMode
                          ? "border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500 bg-gray-800/50"
                          : "border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 bg-white/50"
                    }`}
                    style={isActive ? {
                      background: `linear-gradient(135deg, ${periodColor}, ${periodColor}cc)`,
                      borderColor: periodColor,
                      boxShadow: `0 2px 8px ${periodColor}40`,
                    } : {}}
                  >
                    <PeriodIcon className="h-3.5 w-3.5" />
                    <span>{periodLabel}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Drill name chips ── */}
{/* ── Drill name chips ── */}
{/* ── Drill name chips ── */}
{hasDrillData && drillNames.length > 0 && (
  <div className="flex items-center justify-center gap-2 mt-2.5 flex-wrap">
    {[...drillNames].sort((a, b) => {
      if (a === "global") return -1;
      if (b === "global") return 1;
      return a.localeCompare(b);
    }).map((name) => {
      const DrillIcon = drillIconMap[name] ?? CircleDot;
      const isActive = selectedDrill === name;
      return (
        <button
          key={name}
          onClick={() => setSelectedDrill(name)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
            isActive
              ? "text-white shadow-md scale-[1.02]"
              : isDarkMode
                ? "border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-500 bg-gray-800/50"
                : "border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 bg-white/50"
          }`}
          style={isActive ? {
            background: "linear-gradient(135deg, #6366f1, #6366f1cc)",
            borderColor: "#6366f1",
            boxShadow: "0 2px 8px #6366f140",
          } : {}}
        >
          <DrillIcon className="h-3.5 w-3.5" />
          <span>{name === "global" ? "Global" : name}</span>
        </button>
      );
    })}
  </div>
)}

        </div>{/* end collapsible */}

        {/* Collapse / Expand toggle */}
        {isStuck && (
          <div className="flex justify-center pt-0.5 pb-1">
            <button
              onClick={() => setToolbarCollapsed((v) => !v)}
              className={`flex items-center justify-center w-10 h-4 rounded-full transition-all ${
                isDarkMode
                  ? "text-gray-400 hover:text-gray-100 bg-gray-700/50 hover:bg-gray-600/70"
                  : "text-gray-400 hover:text-gray-600 bg-gray-200/60 hover:bg-gray-300/70"
              }`}
              title={toolbarCollapsed ? t('external_load.show_filters') : t('external_load.hide_filters')}
            >
              {toolbarCollapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
            </button>
          </div>
        )}
      </div>

      {/* ─── No data state ─── */}
      {hasNoData && (
        <div className={`flex items-center justify-center py-12 rounded-lg ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
          <span className="text-sm">{t('external_load.no_data')}</span>
        </div>
      )}

      {/* ─── Histogram charts ─── */}
      <div className="space-y-6" ref={containerRef}>
        {metricOrder
          .filter(metricKey => visibleMetrics.has(metricKey))
          .map((metricKey, index) => {
            const metric = METRICS_BY_KEY[metricKey];
            if (!metric) return null;
            return (
              <div
                key={metric.key}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className={`transition-opacity ${
                  dragItem.current === index ? "opacity-40" : "opacity-100"
                } cursor-grab active:cursor-grabbing`}
              >
                <HistogramChart
                  title={metric.label}
                  unit={metric.unit}
                  color={metric.color}
                  icon={metric.icon}
                  players={dataByKey[metric.key] ?? []}
                  highlightId={highlightId}
                  onBarClick={handleBarClick}
                  isDarkMode={isDarkMode}
                  formatValue={metric.format}
                  isLoading={isTransitioning}
                />
              </div>
            );
          })}
      </div>

      {/* ─── Sticky right sidebar for metric toggles ─── */}
      <MetricVisibilitySidebar
        metrics={DEFAULT_ORDER.map((k) => METRICS_BY_KEY[k]).filter(Boolean)}
        visibleMetrics={visibleMetrics}
        onChange={setVisibleMetrics}
        allKeys={DEFAULT_ORDER}
        storageKey="externalLoad_sidebarOpen"
        isDarkMode={isDarkMode}
      />
    </div>
  );
}